const TOKEN_KEYS = {
  idToken: 'occo_cognito_sso.id_token',
  accessToken: 'occo_cognito_sso.access_token',
  refreshToken: 'occo_cognito_sso.refresh_token',
  expiresAt: 'occo_cognito_sso.expires_at',
};
const PKCE_VERIFIER_KEY = 'occo_cognito_sso.pkce_verifier';
const OAUTH_STATE_KEY = 'occo_cognito_sso.oauth_state';
const OAUTH_RETURN_TO_KEY = 'occo_cognito_sso.return_to';
const EXPIRY_BUFFER_MS = 60 * 1000;

const FORCE_LOGIN_KEY = 'occo_cognito_sso.force_login';
const AUTH_REDIRECT_IN_PROGRESS = 'redirecting_to_auth';

export function markLoginRequired() {
  sessionStorage.setItem(FORCE_LOGIN_KEY, '1');
}

function consumeLoginRequired() {
  if (sessionStorage.getItem(FORCE_LOGIN_KEY) !== '1') {
    return false;
  }
  sessionStorage.removeItem(FORCE_LOGIN_KEY);
  return true;
}

export function isAuthRedirectInProgress(err) {
  return err?.message === AUTH_REDIRECT_IN_PROGRESS || err?.innerError?.message === AUTH_REDIRECT_IN_PROGRESS;
}

const OAUTH_ERROR_HINTS = {
  redirect_mismatch:
    'Register the redirect_uri in Cognito app client Allowed callback URLs (exact match, including trailing slash).',
  unauthorized_client:
    'Use a public SPA app client with Authorization code grant, PKCE, openid/email/profile scopes, and Cognito user pool identity provider enabled.',
  access_denied: 'Sign-in was cancelled or access was denied.',
};

function readEnv(key) {
  if (typeof window !== 'undefined' && window[key] !== undefined) {
    return window[key];
  }
  // Parcel only inlines static process.env.* access (not process.env[key])
  const env = {
    COGNITO_DOMAIN: process.env.COGNITO_DOMAIN,
    POOL_DATA_CLIENT_ID: process.env.POOL_DATA_CLIENT_ID,
    COGNITO_REDIRECT_URI: process.env.COGNITO_REDIRECT_URI,
    COGNITO_LOGOUT_REDIRECT_URI: process.env.COGNITO_LOGOUT_REDIRECT_URI,
  };
  return env[key];
}

function safeParseUrl(uri) {
  try {
    return new URL(uri);
  } catch {
    return null;
  }
}

function normalizeRedirectUri(uri) {
  // Cognito callback/sign-out URLs must match exactly. Strip any trailing slash so
  // both branches below (configured value vs. origin fallback) produce the same form.
  return uri.trim().replace(/\/+$/, '');
}

function ensureStaticHostCallbackPath(uri) {
  // S3/CloudFront sub-path deploys (e.g. /armen-1) have no directory index — only
  // /armen-1/index.html exists. Bare /armen-1 returns NoSuchKey after OAuth.
  const url = safeParseUrl(uri);
  if (!url) return uri;
  const path = url.pathname.replace(/\/+$/, '') || '/';
  if (path === '/' || /\.[a-zA-Z0-9]+$/.test(path)) return uri;
  return `${url.origin}${path}/index.html`;
}

function resolveRedirectUri(envKey, fallbackKey) {
  const configured = readEnv(envKey) || (fallbackKey ? readEnv(fallbackKey) : null);

  if (typeof window !== 'undefined' && window.location?.origin) {
    const currentOrigin = window.location.origin;
    const configuredUrl = configured ? safeParseUrl(configured) : null;
    if (configuredUrl) {
      // Always return to the origin the app is actually running on, but keep the
      // configured callback PATH (e.g. /index.html). This lets a single dev/prod
      // COGNITO_REDIRECT_URI work on localhost without dropping the path — otherwise
      // a sub-path callback page would lose its path and the code would land on the
      return ensureStaticHostCallbackPath(
        normalizeRedirectUri(`${currentOrigin}${configuredUrl.pathname}${configuredUrl.search}`)
      );
    }
    return normalizeRedirectUri(currentOrigin);
  }

  if (!configured) {
    throw new Error(`${envKey} is required`);
  }
  return ensureStaticHostCallbackPath(normalizeRedirectUri(configured));
}

function getConfig() {
  const domain = (readEnv('COGNITO_DOMAIN') || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  const clientId = readEnv('POOL_DATA_CLIENT_ID');
  const redirectUri = resolveRedirectUri('COGNITO_REDIRECT_URI');
  const logoutRedirectUri = resolveRedirectUri('COGNITO_LOGOUT_REDIRECT_URI', 'COGNITO_REDIRECT_URI');

  if (!domain) throw new Error('COGNITO_DOMAIN is required');
  if (!clientId) throw new Error('POOL_DATA_CLIENT_ID is required');

  return { domain, clientId, redirectUri, logoutRedirectUri, scopes: 'openid email profile' };
}

function base64UrlEncode(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function saveTokens({ idToken, accessToken, refreshToken, expiresIn }) {
  localStorage.setItem(TOKEN_KEYS.idToken, idToken);
  localStorage.setItem(TOKEN_KEYS.accessToken, accessToken);
  if (refreshToken) {
    localStorage.setItem(TOKEN_KEYS.refreshToken, refreshToken);
  }
  localStorage.setItem(TOKEN_KEYS.expiresAt, String(Date.now() + Number(expiresIn) * 1000));
}

export function clearSession() {
  Object.values(TOKEN_KEYS).forEach((key) => localStorage.removeItem(key));
}

export function getStoredTokens() {
  return {
    idToken: localStorage.getItem(TOKEN_KEYS.idToken),
    accessToken: localStorage.getItem(TOKEN_KEYS.accessToken),
    refreshToken: localStorage.getItem(TOKEN_KEYS.refreshToken),
    expiresAt: Number(localStorage.getItem(TOKEN_KEYS.expiresAt) || 0),
  };
}

function isTokenExpired(expiresAt) {
  return !expiresAt || Date.now() >= expiresAt - EXPIRY_BUFFER_MS;
}

export function hasStoredSession() {
  const { idToken, expiresAt } = getStoredTokens();
  return Boolean(idToken) && !isTokenExpired(expiresAt);
}

function formatOAuthError(error, errorDescription) {
  const hint = OAUTH_ERROR_HINTS[error];
  const detail = errorDescription || error || 'unknown_error';
  return hint ? `${detail}. ${hint}` : detail;
}

async function requestTokens(body) {
  const { domain } = getConfig();
  const response = await fetch(`https://${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(formatOAuthError(data.error, data.error_description || `Token request failed (${response.status})`));
  }
  return data;
}

async function exchangeAuthorizationCode(code) {
  const { clientId, redirectUri } = getConfig();
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  if (!codeVerifier) {
    throw new Error('Missing PKCE verifier. Restart sign-in.');
  }

  const tokens = await requestTokens(
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
    })
  );
  saveTokens({
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresIn: tokens.expires_in,
  });
}

let refreshInFlight = null;

async function refreshIdToken() {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const { clientId } = getConfig();
    const { refreshToken } = getStoredTokens();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const tokens = await requestTokens(
      new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: refreshToken,
      })
    );
    const { idToken: previousIdToken } = getStoredTokens();
    saveTokens({
      idToken: tokens.id_token || previousIdToken,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token || refreshToken,
      expiresIn: tokens.expires_in,
    });
    return tokens.id_token || previousIdToken;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export async function getIdToken() {
  const { idToken, expiresAt } = getStoredTokens();
  if (!idToken) {
    throw new Error('unauthorized_user');
  }
  if (isTokenExpired(expiresAt)) {
    try {
      // Seamless background refresh — no page reload (mirrors the SDK's getSession).
      return await refreshIdToken();
    } catch {
      // A concurrent refresh may have succeeded while this one failed (rotated token).
      const retry = getStoredTokens();
      if (retry.idToken && !isTokenExpired(retry.expiresAt)) {
        return retry.idToken;
      }
      beginSilentReauth();
    }
  }
  return idToken;
}

function beginSilentReauth() {
  clearSession();
  redirectToAuthorize(globalThis.location.href);
  // Fail fast so callers (guardPage / occoAPI) don't hang; navigation is already queued.
  throw new Error(AUTH_REDIRECT_IN_PROGRESS);
}

function parseOAuthCallback() {
  const url = new URL(globalThis.location.href);
  return {
    code: url.searchParams.get('code'),
    state: url.searchParams.get('state'),
    error: url.searchParams.get('error'),
    errorDescription: url.searchParams.get('error_description'),
  };
}

function cleanOAuthUrl() {
  const url = new URL(globalThis.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('error');
  url.searchParams.delete('error_description');
  globalThis.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}

async function completeAuthorizationCallback(code, state) {
  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  if (!expectedState || state !== expectedState) {
    throw new Error('Invalid OAuth state. Restart sign-in.');
  }
  await exchangeAuthorizationCode(code);
  cleanOAuthUrl();
}

function consumeReturnTo() {
  const returnTo = sessionStorage.getItem(OAUTH_RETURN_TO_KEY);
  sessionStorage.removeItem(OAUTH_RETURN_TO_KEY);
  if (!returnTo) return null;
  // Only honor same-origin targets to avoid being used as an open redirect.
  try {
    if (new URL(returnTo, window.location.origin).origin === window.location.origin) {
      return returnTo;
    }
  } catch {
    /* malformed return target — ignore */
  }
  return null;
}

async function redirectToAuthorize(returnTo) {
  const { domain, clientId, redirectUri, scopes } = getConfig();
  const verifierBytes = new Uint8Array(32);
  crypto.getRandomValues(verifierBytes);
  const codeVerifier = base64UrlEncode(verifierBytes);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  const codeChallenge = base64UrlEncode(digest);

  const stateBytes = new Uint8Array(16);
  crypto.getRandomValues(stateBytes);
  const oauthState = base64UrlEncode(stateBytes);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, oauthState);
  if (returnTo) {
    sessionStorage.setItem(OAUTH_RETURN_TO_KEY, returnTo);
  } else {
    sessionStorage.removeItem(OAUTH_RETURN_TO_KEY);
  }

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: scopes,
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
    state: oauthState,
  });

  window.location.assign(`https://${domain}/oauth2/authorize?${params}`);
}

export function signOut({ logoutRedirectUri } = {}) {
  const { domain, clientId, logoutRedirectUri: defaultLogoutUri } = getConfig();
  clearSession();
  const params = new URLSearchParams({
    client_id: clientId,
    logout_uri: logoutRedirectUri || defaultLogoutUri,
  });
  window.location.assign(`https://${domain}/logout?${params}`);
}

export async function runLoginFlow({ redirectAfterLogin, onSuccess, onError }) {
  const { error, errorDescription, code, state } = parseOAuthCallback();

  if (error) {
    cleanOAuthUrl();
    onError?.(new Error(formatOAuthError(error, errorDescription)));
    return;
  }

  if (code) {
    try {
      await completeAuthorizationCallback(code, state);
      if (onSuccess) {
        await onSuccess();
      }
      window.location.href = consumeReturnTo() || redirectAfterLogin;
    } catch (err) {
      cleanOAuthUrl();
      onError?.(err);
    }
    return;
  }

  if (consumeLoginRequired()) {
    clearSession();
  } else if (hasStoredSession()) {
    try {
      await getIdToken();
      // Re-sync role/username even when reusing a valid session, otherwise a session
      // restored without onSuccess leaves the portal showing the generic 'User'.
      // Best-effort: a sync failure must not block an already-authenticated user.
      try {
        if (onSuccess) await onSuccess();
      } catch {
        /* destination page reloads user info itself */
      }
      window.location.href = redirectAfterLogin;
      return;
    } catch (err) {
      clearSession();
      if (err?.message === AUTH_REDIRECT_IN_PROGRESS) {
        return;
      }
    }
  }

  try {
    await redirectToAuthorize();
  } catch (err) {
    onError?.(err);
  }
}
